/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Troubleshoot events", description = "Diagnose common Events problems — missing events, blocked or flagged events, unexpected warehouse columns, rejected writes, and data that doesn't match your previous tool.", lastUpdatedDate = new Date(1782345600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Troubleshoot events",
        description: "Diagnose common Events problems — missing events, blocked or flagged events, unexpected warehouse columns, rejected writes, and data that doesn't match your previous tool.",
        lastUpdatedDate: "Thu Jun 25 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Events aren't arriving, or volume is lower than expected",
          "depth": 0,
          "id": "events-arent-arriving-or-volume-is-lower-than-expected"
        }, {
          "value": "An event is blocked or flagged",
          "depth": 0,
          "id": "an-event-is-blocked-or-flagged"
        }, {
          "value": "Unexpected or extra columns in the warehouse",
          "depth": 0,
          "id": "unexpected-or-extra-columns-in-the-warehouse"
        }, {
          "value": "An event was rejected and not written",
          "depth": 0,
          "id": "an-event-was-rejected-and-not-written"
        }, {
          "value": "Data doesn't match my previous tool",
          "depth": 0,
          "id": "data-doesnt-match-my-previous-tool"
        }, {
          "value": "Still stuck?",
          "depth": 0,
          "id": "still-stuck"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      ul: "ul",
      li: "li",
      a: "a",
      code: "code",
      hr: "hr",
      h2: "h2",
      strong: "strong"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Start here when something looks wrong with your event data. Each section starts from a symptom, points to the likely cause, and links to the fix."
        })
      }), "\n", _jsx(_components.p, {
        children: "Start with these two tools:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.a, {
            href: "/events/sources/overview#debugger",
            children: "debugger"
          }), " shows individual events as they arrive, so you can confirm exactly what Hightouch received."]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.code, {
            children: "event_violations"
          }), " table in your warehouse records every event that failed contract validation, along with the reason. See ", _jsx(_components.a, {
            href: "/events/contracts/violations",
            children: "handling violations"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "events-arent-arriving-or-volume-is-lower-than-expected",
        children: "Events aren't arriving, or volume is lower than expected"
      }), "\n", _jsx(_components.p, {
        children: "Work through these in order:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Confirm receipt in the debugger."
          }), " If events don't appear there, the problem is upstream of Hightouch — the SDK isn't firing, or the write key or region is wrong. Check that you're using the write key from your event source."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Expect some client-side loss."
          }), " Browser events are dropped by ad blockers, privacy settings such as Safari ITP, and cookie limits. This is inherent to client-side collection, not a setup error. Reduce it with ", _jsx(_components.a, {
            href: "/events/first-party-tracking",
            children: "first-party tracking"
          }), ", and move business-critical events to ", _jsx(_components.a, {
            href: "/events/sdks/http",
            children: "server-side collection"
          }), ". See ", _jsx(_components.a, {
            href: "/events/implementation-plan#plan-for-coverage",
            children: "Plan for coverage"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check whether a contract is blocking events."
          }), " If enforcement is set to block, undeclared or invalid events are dropped from your main tables. See the next section."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "an-event-is-blocked-or-flagged",
        children: "An event is blocked or flagged"
      }), "\n", _jsxs(_components.p, {
        children: ["A contract ", _jsx(_components.a, {
          href: "/events/contracts/violations",
          children: "violation"
        }), " means an event didn't satisfy the contract applied to its source. The match is exact, so the usual causes are small mismatches:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The event name doesn't match the contract, including capitalization and whitespace."
        }), "\n", _jsxs(_components.li, {
          children: ["A field is in the wrong section — for example, sent under ", _jsx(_components.code, {
            children: "properties"
          }), " when the contract expects it under ", _jsx(_components.code, {
            children: "context"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "A field's type doesn't match the schema."
        }), "\n", _jsx(_components.li, {
          children: "The contract is empty while set to block undeclared events, so everything is blocked."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Work through these with ", _jsx(_components.a, {
          href: "/events/contracts/violations#diagnose-why-an-event-was-blocked-or-flagged",
          children: "Diagnose why an event was blocked or flagged"
        }), ", which maps each cause to its fix."]
      }), "\n", _jsx(_components.h2, {
        id: "unexpected-or-extra-columns-in-the-warehouse",
        children: "Unexpected or extra columns in the warehouse"
      }), "\n", _jsx(_components.p, {
        children: "Your warehouse tables mirror the data you send — Hightouch structures it but doesn't clean it. Extra or odd columns usually trace back to the input:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A misspelled or inconsistently named property creates a new column whenever it varies."
        }), "\n", _jsxs(_components.li, {
          children: ["Malformed URL or campaign parameters — for example, a missing ", _jsx(_components.code, {
            children: "="
          }), " in a UTM tag — land as unexpected fields."]
        }), "\n", _jsxs(_components.li, {
          children: ["Nested objects are flattened into separate columns (", _jsx(_components.code, {
            children: "company.name"
          }), " becomes ", _jsx(_components.code, {
            children: "company_name"
          }), ")."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Validate the inbound data before changing anything in the warehouse. See ", _jsx(_components.a, {
          href: "/events/warehouses/schema",
          children: "Warehouse schema"
        }), " for how columns and types are created."]
      }), "\n", _jsx(_components.h2, {
        id: "an-event-was-rejected-and-not-written",
        children: "An event was rejected and not written"
      }), "\n", _jsxs(_components.p, {
        children: ["If an event's value doesn't match the existing column type — for example, a string where the column is a number — Hightouch can't write it. It ", _jsx(_components.a, {
          href: "/events/warehouses/error-handling",
          children: "retries the event for 48 hours"
        }), ", so correcting the type in your warehouse lets the event sync without data loss. See ", _jsx(_components.a, {
          href: "/events/warehouses/error-handling",
          children: "Error handling"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "data-doesnt-match-my-previous-tool",
        children: "Data doesn't match my previous tool"
      }), "\n", _jsxs(_components.p, {
        children: ["During a migration, some difference between systems is normal — timing, deduplication, ad blockers, and Hightouch's stricter blocking all cause minor variation. Before treating a gap as a bug, see ", _jsx(_components.a, {
          href: "/events/migration-guide/validating-your-migration#what-differences-to-expect-between-your-old-provider-and-hightouch-events",
          children: "what differences to expect"
        }), " and the parity checks in ", _jsx(_components.a, {
          href: "/events/migration-guide/validating-your-migration",
          children: "Validate your migration"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "still-stuck",
        children: "Still stuck?"
      }), "\n", _jsxs(_components.p, {
        children: ["Set up ", _jsx(_components.a, {
          href: "/events/monitoring",
          children: "monitoring and alerts"
        }), " so you catch issues early, and ", _jsx(PylonLink, {
          pylonText: "Hi, I need help troubleshooting my Hightouch Events setup.",
          linkText: "contact support"
        }), " if you can't resolve a problem."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
